import { Box, Button, IconButton } from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

interface Day {
    day_count: number;
    day_date: string;
    title: string;
}

interface DaysNavigatorProps {
    days?: Day[];
    selectedDay?: number;
    onDaySelect?: (day: number) => void;
}

export default function DaysNavigator({
    days = [],
    selectedDay = 0,
    onDaySelect,
}: DaysNavigatorProps) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showLeftIndicator, setShowLeftIndicator] = useState(false);
    const [showRightIndicator, setShowRightIndicator] = useState(false);

    const handleDayClick = (dayIndex: number) => {
        onDaySelect?.(dayIndex);
    };

    const totalDays = days.length || 5;

    const checkScroll = () => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;

        // Show left indicator if scrolled right
        setShowLeftIndicator(scrollLeft > 10);

        // Show right indicator if there's more content to scroll
        setShowRightIndicator(scrollLeft < scrollWidth - clientWidth - 10);
    };

    const scroll = (direction: 'left' | 'right') => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const scrollAmount = 300;
        container.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
        });
    };

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        // Initial check
        checkScroll();

        // Add scroll listener
        container.addEventListener('scroll', checkScroll);

        // Add resize listener to recheck on window resize
        window.addEventListener('resize', checkScroll);

        return () => {
            container.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        };
    }, [totalDays]);

    return (
        <Box
            sx={{
                width: "100%",
                backgroundColor: "#ffffff",
                padding: { xs: "12px 8px", md: "20px 12px" },
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                position: "relative",
            }}
        >
            {/* Left Scroll Indicator */}
            {showLeftIndicator && (
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "80px",
                        background: "linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.7) 50%, transparent 100%)",
                        pointerEvents: "none",
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "8px",
                    }}
                >
                    <IconButton
                        onClick={() => scroll('left')}
                        sx={{
                            pointerEvents: "auto",
                            backgroundColor: "#1a9a9a",
                            color: "white",
                            width: "32px",
                            height: "32px",
                            "&:hover": {
                                backgroundColor: "#157878",
                            },
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        }}
                    >
                        <MdChevronLeft style={{ fontSize: "24px" }} />
                    </IconButton>
                </Box>
            )}

            {/* Right Scroll Indicator */}
            {showRightIndicator && (
                <Box
                    sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: "80px",
                        background: "linear-gradient(to left, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.7) 50%, transparent 100%)",
                        pointerEvents: "none",
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        paddingRight: "8px",
                    }}
                >
                    <IconButton
                        onClick={() => scroll('right')}
                        sx={{
                            pointerEvents: "auto",
                            backgroundColor: "#1a9a9a",
                            color: "white",
                            width: "32px",
                            height: "32px",
                            "&:hover": {
                                backgroundColor: "#157878",
                            },
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        }}
                    >
                        <MdChevronRight style={{ fontSize: "24px" }} />
                    </IconButton>
                </Box>
            )}

            <Box
                ref={scrollContainerRef}
                sx={{
                    display: "flex",
                    gap: { xs: "8px", md: "16px" },
                    mx: { xs: 4, md: 16 },
                    justifyContent: "space-between",
                    overflowX: "auto",
                    overflowY: "hidden",
                    "&::-webkit-scrollbar": {
                        display: "none",
                    },
                    scrollbarWidth: "none", // Firefox
                    msOverflowStyle: "none", // IE and Edge
                    scrollBehavior: "smooth",
                }}
            >
                {Array.from({ length: totalDays }, (_, index) => {
                    const isActive = index === selectedDay;
                    const dayData = days[index];

                    return (
                        <Box
                            key={index}
                            sx={{
                                position: "relative",
                                flexShrink: 0,
                            }}
                        >
                            <Button
                                onClick={() => handleDayClick(index)}
                                sx={{
                                    minWidth: { xs: "100px", md: "160px" },
                                    height: { xs: "36px", md: "48px" },
                                    borderRadius: "8px",
                                    fontSize: { xs: "14px", md: "16px" },
                                    fontWeight: 600,
                                    textTransform: "none",
                                    transition: "all 0.3s ease",
                                    backgroundColor: isActive ? "#1a9a9a" : "#f5f5f5",
                                    color: isActive ? "#ffffff" : "#333333",
                                    "&:hover": {
                                        backgroundColor: isActive ? "#157878" : "#e8e8e8",
                                        transform: "translateY(-2px)",
                                        boxShadow: isActive
                                            ? "0 4px 12px rgba(26, 154, 154, 0.3)"
                                            : "0 2px 8px rgba(0, 0, 0, 0.1)",
                                    },
                                    "&:active": {
                                        transform: "translateY(0)",
                                    },
                                }}
                            >
                                {dayData ? `Day ${dayData.day_count}` : `Day ${index + 1}`}
                            </Button>
                            {isActive && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        bottom: "-20px",
                                        left: 0,
                                        right: 0,
                                        height: "4px",
                                        backgroundColor: "#1a9a9a",
                                        borderRadius: "2px 2px 0 0",
                                    }}
                                />
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
